// =====================================================
// FINANCIAL ANALYSIS UTILITIES
// Automatic calculations for EBITDA, Multiples, and Valuations
// =====================================================

import { SubmitDealFormData } from '@/types/submit-deal.types';

export interface FinancialAnalysis {
  // EBITDA Analysis
  reportedEbitda: number;
  totalAddbacks: number;
  adjustedEbitda: number;
  
  // Revenue Analysis
  avgRevenue: number;
  revenueGrowthRate: number;
  
  // Valuation Multiples
  revenueMultiple: number;
  ebitdaMultiple: number;
  
  // Cash Flow Analysis
  sellerDiscretionaryCashFlow: number; // SDE
  debtServiceCoverageRatio: number;
  
  // Key Metrics
  grossMargin: number;
  ebitdaMargin: number;
  customerConcentrationRisk: 'low' | 'medium' | 'high';
  ownerDependencyRisk: 'low' | 'medium' | 'high';
  
  // Valuation Range
  valuationLow: number;
  valuationMid: number;
  valuationHigh: number;
}

/**
 * Performs comprehensive financial analysis on deal submission data
 */
export function performFinancialAnalysis(formData: SubmitDealFormData): FinancialAnalysis {
  const { financials, addbacks, operations, projections } = formData;

  // Calculate total addbacks
  const totalAddbacks = 
    (addbacks.ownerSalary || 0) +
    (addbacks.autoExpense || 0) +
    (addbacks.travelEntertainment || 0) +
    (addbacks.nonRecurringLegal || 0) +
    (addbacks.otherDiscretionary || 0);

  // Use Year 1 EBITDA as reported EBITDA
  const reportedEbitda = financials.ebitdaYear1 || 0;
  const adjustedEbitda = reportedEbitda + totalAddbacks;

  // Revenue analysis
  const revenues = [
    financials.revenueYear1 || 0,
    financials.revenueYear2 || 0,
    financials.revenueYear3 || 0,
  ].filter(r => r > 0);
  
  const avgRevenue = revenues.length > 0 
    ? revenues.reduce((a, b) => a + b, 0) / revenues.length 
    : 0;

  // Calculate revenue growth rate (Year 1 to Year 3)
  let revenueGrowthRate = 0;
  if (financials.revenueYear3 > 0 && financials.revenueYear1 > 0) {
    revenueGrowthRate = ((financials.revenueYear3 / financials.revenueYear1) - 1) * 100;
  }

  // Valuation multiples (based on asking price)
  const askingPrice = financials.askingPrice || 0;
  const revenueMultiple = avgRevenue > 0 ? askingPrice / avgRevenue : 0;
  const ebitdaMultiple = adjustedEbitda > 0 ? askingPrice / adjustedEbitda : 0;

  // Seller Discretionary Earnings (SDE) - common for small businesses
  const sellerDiscretionaryCashFlow = adjustedEbitda + (addbacks.ownerSalary || 0);

  // Debt Service Coverage Ratio (simplified)
  const annualDebtPayment = financials.inheritedDebt > 0 ? financials.inheritedDebt * 0.1 : 1; // Assume 10% annual payment
  const debtServiceCoverageRatio = financials.cashFlowEquity / annualDebtPayment;

  // Gross and EBITDA margins
  const grossMargin = avgRevenue > 0 ? ((avgRevenue - (avgRevenue * 0.4)) / avgRevenue) * 100 : 0; // Simplified
  const ebitdaMargin = avgRevenue > 0 ? (adjustedEbitda / avgRevenue) * 100 : 0;

  // Risk assessments
  const customerConcentrationRisk = getConcentrationRisk(operations.customerConcentration);
  const ownerDependencyRisk = getOwnerDependencyRisk(operations.ownerHoursPerWeek, operations.ownerType);

  // Valuation range calculation
  const { valuationLow, valuationMid, valuationHigh } = calculateValuationRange(
    adjustedEbitda,
    sellerDiscretionaryCashFlow,
    revenueGrowthRate,
    customerConcentrationRisk,
    ownerDependencyRisk
  );

  return {
    reportedEbitda,
    totalAddbacks,
    adjustedEbitda,
    avgRevenue,
    revenueGrowthRate,
    revenueMultiple,
    ebitdaMultiple,
    sellerDiscretionaryCashFlow,
    debtServiceCoverageRatio,
    grossMargin,
    ebitdaMargin,
    customerConcentrationRisk,
    ownerDependencyRisk,
    valuationLow,
    valuationMid,
    valuationHigh,
  };
}

function getConcentrationRisk(concentration: number): 'low' | 'medium' | 'high' {
  if (concentration <= 15) return 'low';
  if (concentration <= 30) return 'medium';
  return 'high';
}

function getOwnerDependencyRisk(hoursPerWeek: number, ownerType: string): 'low' | 'medium' | 'high' {
  if (ownerType === 'absentee' || hoursPerWeek <= 10) return 'low';
  if (ownerType === 'semiActive' || hoursPerWeek <= 30) return 'medium';
  return 'high';
}

function calculateValuationRange(
  adjustedEbitda: number,
  sde: number,
  growthRate: number,
  customerRisk: string,
  ownerRisk: string
): { valuationLow: number; valuationMid: number; valuationHigh: number } {
  // Base multiples (industry standard ranges)
  let baseMultipleLow = 2.5;
  let baseMultipleMid = 3.5;
  let baseMultipleHigh = 5.0;

  // Adjust for growth rate
  if (growthRate > 20) {
    baseMultipleLow += 0.5;
    baseMultipleMid += 0.75;
    baseMultipleHigh += 1.0;
  } else if (growthRate < 0) {
    baseMultipleLow -= 0.5;
    baseMultipleMid -= 0.5;
    baseMultipleHigh -= 0.5;
  }

  // Adjust for risks
  if (customerRisk === 'high') {
    baseMultipleLow -= 0.5;
    baseMultipleMid -= 0.5;
    baseMultipleHigh -= 0.5;
  }
  if (ownerRisk === 'high') {
    baseMultipleLow -= 0.25;
    baseMultipleMid -= 0.25;
    baseMultipleHigh -= 0.25;
  }

  // Use SDE for smaller businesses (< $1M EBITDA), EBITDA for larger
  const baseValue = adjustedEbitda < 1000000 ? sde : adjustedEbitda;

  return {
    valuationLow: Math.round(baseValue * Math.max(baseMultipleLow, 1.5)),
    valuationMid: Math.round(baseValue * Math.max(baseMultipleMid, 2.5)),
    valuationHigh: Math.round(baseValue * Math.max(baseMultipleHigh, 4.0)),
  };
}

/**
 * Format currency for display
 */
export function formatCurrency(amount: number, currency: string = 'USD'): string {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency,
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(amount);
}

/**
 * Format percentage for display
 */
export function formatPercent(value: number): string {
  return `${value.toFixed(1)}%`;
}
